import React from "react";
import { MessageCircle, CalendarCheck, Save } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Header } from "@/pages/admin/AdminBlog";

export default function AdminSettings() {
  const [form, setForm] = React.useState({
    whatsapp_number: "",
    whatsapp_message: "",
    calendly_url: "",
  });
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    api.get("/settings").then((r) => {
      setForm(r.data);
      setLoading(false);
    });
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/settings", form);
      toast.success("Settings updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-slate-400 text-sm">Loading…</div>;

  return (
    <div data-testid="admin-settings">
      <Header title="Site Settings" />

      <form onSubmit={save} className="mt-8 max-w-2xl space-y-6">
        <div className="card-luxury p-7">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-9 h-9 rounded-lg border border-emerald-500/30 bg-emerald-500/10 flex items-center justify-center">
              <MessageCircle className="w-4 h-4 text-emerald-400" />
            </div>
            <div>
              <div className="text-white font-medium">WhatsApp</div>
              <div className="text-slate-400 text-xs">Number used for the floating chat button.</div>
            </div>
          </div>
          <div className="space-y-4">
            <Field label="WhatsApp number (with country code)">
              <Input
                value={form.whatsapp_number}
                onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })}
                placeholder="+971501234567"
                data-testid="settings-whatsapp-number"
                className="bg-[#0B1221] border-white/10 text-white focus:border-sky-500"
              />
            </Field>
            <Field label="Pre-filled message">
              <Textarea
                rows={2}
                value={form.whatsapp_message}
                onChange={(e) => setForm({ ...form, whatsapp_message: e.target.value })}
                data-testid="settings-whatsapp-message"
                className="bg-[#0B1221] border-white/10 text-white focus:border-sky-500 resize-none"
              />
            </Field>
          </div>
        </div>

        <div className="card-luxury p-7">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-9 h-9 rounded-lg border border-[#D4AF37]/30 bg-[#D4AF37]/10 flex items-center justify-center">
              <CalendarCheck className="w-4 h-4 text-[#D4AF37]" />
            </div>
            <div>
              <div className="text-white font-medium">Calendly</div>
              <div className="text-slate-400 text-xs">URL used for "Book Consultation" buttons.</div>
            </div>
          </div>
          <Field label="Calendly URL">
            <Input
              value={form.calendly_url}
              onChange={(e) => setForm({ ...form, calendly_url: e.target.value })}
              placeholder="https://calendly.com/your-handle/consultation"
              data-testid="settings-calendly-url"
              className="bg-[#0B1221] border-white/10 text-white focus:border-sky-500"
            />
          </Field>
        </div>

        <button type="submit" disabled={saving} className="btn-primary" data-testid="settings-save">
          {saving ? "Saving…" : "Save settings"} <Save className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-slate-400 mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}
